"use client";

import React, { useState, useEffect } from "react";
import { useAuth } from "./Auth";
import { useToast } from "./Toast";

interface User {
  id: string;
  name: string;
  email: string;
  role: "admin" | "user";
  createdAt: string;
  lastLogin?: string;
  securityLevel: 'low' | 'medium' | 'high';
  mfaEnabled?: boolean;
  lastPasswordChange?: string;
}

export default function UserManagement() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "user" as "admin" | "user"
  });
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  // Carregar usuários
  useEffect(() => {
    loadUsers();
  }, []);

  // Validação de senha
  const validatePassword = (password: string): string[] => {
    const errors: string[] = [];
    
    if (!password || password.length < 8) {
      errors.push("Senha deve ter no mínimo 8 caracteres");
    }
    
    if (password.length > 128) {
      errors.push("Senha deve ter no máximo 128 caracteres");
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push("Senha deve conter pelo menos uma letra maiúscula");
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push("Senha deve conter pelo menos uma letra minúscula");
    }
    
    if (!/\d/.test(password)) {
      errors.push("Senha deve conter pelo menos um número");
    }
    
    if (!/[@$!%*?&]/.test(password)) {
      errors.push("Senha deve conter pelo menos um caractere especial (@$!%*?&)");
    }
    
    // Verificar senhas comuns
    const commonPasswords = ['password', '123456', 'admin', 'qwerty', 'senha'];
    if (commonPasswords.some(common => password.toLowerCase().includes(common))) {
      errors.push("Senha muito comum, escolha uma mais segura");
    }
    
    return errors;
  };

  const loadUsers = async () => {
    try {
      const response = await fetch("/api/admin/users");
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else {
        const error = await response.json();
        addToast(error.error || "Erro ao carregar usuários", "error");
      }
    } catch (error) {
      console.error("Erro ao carregar usuários:", error);
      addToast("Erro ao carregar usuários", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar senha no frontend primeiro
    const errors = validatePassword(formData.password);
    if (errors.length > 0) {
      setPasswordErrors(errors);
      addToast("Corrija os erros na senha", "error");
      return;
    }
    
    setIsCreating(true);
    setPasswordErrors([]);

    try {
      const response = await fetch("/api/admin/users", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const newUser = await response.json();
        setUsers([...users, newUser]);
        setFormData({ name: "", email: "", password: "", role: "user" });
        setShowCreateForm(false);
        addToast(newUser.message || "Usuário criado com sucesso!", "success");
        
        // Mostrar dicas de segurança se disponíveis
        if (newUser.securityTips) {
          newUser.securityTips.forEach((tip: string) => {
            setTimeout(() => addToast(tip, "info"), 1000);
          });
        }
      } else {
        const error = await response.json();
        if (error.details && Array.isArray(error.details)) {
          setPasswordErrors(error.details);
        }
        addToast(error.error || "Erro ao criar usuário", "error");
      }
    } catch (error) {
      console.error("Erro ao criar usuário:", error);
      addToast("Erro ao criar usuário", "error");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm("Tem certeza que deseja excluir este usuário?")) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/users?id=${userId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setUsers(users.filter(u => u.id !== userId));
        addToast("Usuário excluído com sucesso!", "success");
      } else {
        const error = await response.json();
        addToast(error.error || "Erro ao excluir usuário", "error");
      }
    } catch (error) {
      console.error("Erro ao excluir usuário:", error);
      addToast("Erro ao excluir usuário", "error");
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-primary/90 text-white p-6 rounded-2xl shadow-xl">
        <h3 className="text-2xl md:text-3xl font-bold mb-2">👥 Gerenciamento de Usuários</h3>
        <p className="opacity-90">Administre todos os usuários do sistema</p>
      </div>

      {/* Create User Button */}
      <div className="flex justify-between items-center">
        <div>
          <h4 className="text-lg font-semibold text-gray-800">
            Total de Usuários: {users.length}
          </h4>
          <p className="text-sm text-gray-500">
            {users.filter(u => u.role === "admin").length} Admin(s), {users.filter(u => u.role === "user").length} Usuário(s)
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all duration-200 transform hover:scale-105 font-medium"
        >
          {showCreateForm ? "Cancelar" : "+ Novo Usuário"}
        </button>
      </div>

      {/* Create User Form */}
      {showCreateForm && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h4 className="text-xl font-bold mb-6 text-gray-800">👤 Criar Novo Usuário</h4>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome Completo
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                  placeholder="João Silva"
                  minLength={2}
                  maxLength={100}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                  placeholder="joao@exemplo.com"
                  maxLength={255}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Senha
                </label>
                <input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => {
                    setFormData({ ...formData, password: e.target.value });
                    setPasswordErrors(validatePassword(e.target.value));
                  }}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                  placeholder="Mínimo 8 caracteres"
                  minLength={8}
                  maxLength={128}
                />
                {passwordErrors.length > 0 && (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-xs font-medium text-red-800 mb-1">Requisitos de senha:</p>
                    <ul className="text-xs text-red-600 space-y-1">
                      {passwordErrors.map((error, index) => (
                        <li key={index}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Função
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as "admin" | "user" })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                >
                  <option value="user">Usuário</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setPasswordErrors([]);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-all duration-200"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isCreating || passwordErrors.length > 0}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Criando...
                  </div>
                ) : (
                  "Criar Usuário"
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Users List */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuário
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Função
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Segurança
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data de Criação
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((userItem) => (
                <tr key={userItem.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-gradient-to-r from-primary to-primary/80 rounded-full flex items-center justify-center text-white text-sm font-medium">
                        {userItem.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">
                          {userItem.name}
                        </div>
                        {userItem.id === user?.id && (
                          <div className="text-xs text-primary">Você</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{userItem.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      userItem.role === "admin"
                        ? "bg-purple-100 text-purple-800"
                        : "bg-blue-100 text-blue-800"
                    }`}>
                      {userItem.role === "admin" ? "👑 Admin" : "👤 Usuário"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-1">
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded ${
                          userItem.securityLevel === 'high'
                            ? "bg-green-100 text-green-800"
                            : userItem.securityLevel === 'medium'
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-red-100 text-red-800"
                        }`}>
                          {userItem.securityLevel === 'high' ? '🛡️ Alta' : 
                           userItem.securityLevel === 'medium' ? '🔒 Média' : '⚠️ Baixa'}
                        </span>
                        {userItem.mfaEnabled && (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded bg-indigo-100 text-indigo-800">
                            🔐 MFA
                          </span>
                        )}
                      </div>
                      {userItem.lastPasswordChange && (
                        <div className="text-xs text-gray-500">
                          Senha: {new Date(userItem.lastPasswordChange).toLocaleDateString('pt-BR')}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex flex-col">
                      <span>{new Date(userItem.createdAt).toLocaleDateString("pt-BR")}</span>
                      {userItem.lastLogin && (
                        <span className="text-xs text-gray-400">
                          Último login: {new Date(userItem.lastLogin).toLocaleDateString('pt-BR')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {userItem.id !== user?.id && (
                      <button
                        onClick={() => handleDeleteUser(userItem.id)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                      >
                        🗑️ Excluir
                      </button>
                    )}
                    {userItem.id === user?.id && (
                      <span className="text-gray-400 text-xs">Não pode excluir</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {users.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">👥</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum usuário encontrado</h3>
            <p className="text-gray-500">Clique em "Novo Usuário" para criar o primeiro usuário.</p>
          </div>
        )}
      </div>
    </div>
  );
}
